import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator } from "lucide-react";

export default function TemperatureConverter() {
  const [temperature, setTemperature] = useState("");
  const [fromUnit, setFromUnit] = useState("celsius");
  const [celsius, setCelsius] = useState<number | null>(null);
  const [kelvin, setKelvin] = useState<number | null>(null);
  const [fahrenheit, setFahrenheit] = useState<number | null>(null);

  const convertTemperature = (value: string, from: string) => {
    const temp = parseFloat(value);
    if (isNaN(temp)) {
      setCelsius(null);
      setKelvin(null);
      setFahrenheit(null);
      return;
    }

    let celsiusTemp: number;
    
    if (from === "celsius") {
      celsiusTemp = temp;
    } else if (from === "kelvin") {
      celsiusTemp = temp - 273.15;
    } else {
      celsiusTemp = (temp - 32) * 5/9;
    }

    setCelsius(celsiusTemp);
    setKelvin(celsiusTemp + 273.15);
    setFahrenheit(celsiusTemp * 9/5 + 32);
  };

  const handleTemperatureChange = (value: string) => {
    setTemperature(value);
    convertTemperature(value, fromUnit);
  };

  const handleUnitChange = (unit: string) => {
    setFromUnit(unit);
    convertTemperature(temperature, unit);
  };

  return (
    <PageLayout calculatorName="Temperature Converter" calculatorPath="/calculator/temperature">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Temperature Conversion</CardTitle>
            <CardDescription>
              Enter temperature in any unit to see conversions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature</Label>
                <div className="flex gap-3">
                  <Input
                    id="temperature"
                    type="number"
                    step="0.01"
                    placeholder="Enter temperature"
                    value={temperature}
                    onChange={(e) => handleTemperatureChange(e.target.value)}
                    className="flex-1"
                  />
                  <Select value={fromUnit} onValueChange={handleUnitChange}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celsius">Celsius (°C)</SelectItem>
                      <SelectItem value="kelvin">Kelvin (K)</SelectItem>
                      <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {celsius !== null && (
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Celsius</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-primary">
                      {celsius.toFixed(2)} °C
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Kelvin</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-primary">
                      {kelvin!.toFixed(2)} K
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Fahrenheit</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-primary">
                      {fahrenheit!.toFixed(2)} °F
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Conversion Formulas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Celsius to Kelvin: K = °C + 273.15</p>
                <p>• Celsius to Fahrenheit: °F = °C × 9/5 + 32</p>
                <p>• Kelvin to Celsius: °C = K - 273.15</p>
                <p>• Fahrenheit to Celsius: °C = (°F - 32) × 5/9</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 0°C = 273.15 K = 32°F (freezing point of water)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
